import React, { useState, useEffect } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';
import { User, Shield, CreditCard, Lock, Mail, Save, AlertCircle, Eye, EyeOff } from 'lucide-react';

interface Props {
    session: Session;
}

export const Profile: React.FC<Props> = ({ session }) => {
    const [profile, setProfile] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    // Form State
    const [fullName, setFullName] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // Visibility State
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // UI State
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchProfile = async () => {
            const { data, error } = await supabase
                .from('user_profiles')
                .select('*')
                .eq('id', session.user.id)
                .single();

            if (data) {
                setProfile(data);
                setFullName(data.full_name || '');
            }
            setLoading(false);
        };

        fetchProfile();
    }, [session]);

    const handleUpdateProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setMessage(null);

        try {
            // 1. Update Name in public.user_profiles
            if (fullName !== profile?.full_name) {
                const { error } = await supabase
                    .from('user_profiles')
                    .update({ full_name: fullName })
                    .eq('id', session.user.id);

                if (error) throw error;
            }

            // 2. Update Password if requested
            if (newPassword || confirmPassword) {
                if (!currentPassword) {
                    throw new Error("Please enter your current password to change it.");
                }

                // Verify current password by attempting to sign in
                const { error: signInError } = await supabase.auth.signInWithPassword({
                    email: session.user.email!,
                    password: currentPassword
                });

                if (signInError) {
                    throw new Error("Incorrect current password.");
                }

                if (newPassword !== confirmPassword) {
                    throw new Error("New passwords do not match.");
                }
                if (newPassword.length < 6) {
                    throw new Error("Password must be at least 6 characters long.");
                }

                const { error: authError } = await supabase.auth.updateUser({
                    password: newPassword
                });

                if (authError) throw authError;

                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
            }

            setMessage({ type: 'success', text: 'Profile updated successfully!' });
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message || 'An error occurred while updating.' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-finance-accent border-r-transparent rounded-full animate-spin"></div></div>;
    }

    return (
        <main className="flex-grow w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
            <div className="mb-8">
                <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight mb-2">User Settings</h1>
                <p className="text-gray-400">Manage your profile, security, and view your current subscription plan.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

                {/* Left Column: Form */}
                <div className="md:col-span-2 space-y-6">
                    <form onSubmit={handleUpdateProfile} className="bg-[#0b0f19] border border-gray-800 rounded-2xl p-6 lg:p-8">
                        <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                            <User className="text-finance-accent w-5 h-5" />
                            Personal Information
                        </h2>

                        {message && (
                            <div className={`p-4 mb-6 rounded-lg flex items-center gap-3 text-sm font-medium ${message.type === 'error' ? 'bg-red-500/10 text-red-500 border border-red-500/20' : 'bg-green-500/10 text-green-500 border border-green-500/20'}`}>
                                <AlertCircle className="w-5 h-5" />
                                {message.text}
                            </div>
                        )}

                        <div className="space-y-4 mb-8">
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Email Address</label>
                                <div className="flex items-center gap-3 bg-gray-900/50 border border-gray-800 rounded-lg px-4 py-3 text-gray-400 opacity-70 cursor-not-allowed">
                                    <Mail className="w-4 h-4" />
                                    {session.user.email}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">To change your email, please contact support.</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Full Name</label>
                                <input
                                    type="text"
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    className="w-full bg-finance-dark text-white border border-gray-800 rounded-lg px-4 py-3 focus:outline-none focus:border-finance-accent focus:ring-1 focus:ring-finance-accent transition-colors"
                                    placeholder="John Doe"
                                />
                            </div>
                        </div>

                        <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2 pt-6 border-t border-gray-800">
                            <Lock className="text-finance-accent w-5 h-5" />
                            Change Password
                        </h2>

                        <div className="space-y-4 mb-8">
                            <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 mb-4">
                                <p className="text-sm text-gray-400">Leave these fields blank if you do not wish to change your password.</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Current Password</label>
                                <div className="relative">
                                    <input
                                        type={showCurrentPassword ? "text" : "password"}
                                        value={currentPassword}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                        className="w-full bg-finance-dark text-white border border-gray-800 rounded-lg px-4 py-3 focus:outline-none focus:border-finance-accent focus:ring-1 focus:ring-finance-accent transition-colors"
                                        placeholder="••••••••"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white focus:outline-none"
                                    >
                                        {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">New Password</label>
                                <div className="relative">
                                    <input
                                        type={showNewPassword ? "text" : "password"}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        className="w-full bg-finance-dark text-white border border-gray-800 rounded-lg px-4 py-3 focus:outline-none focus:border-finance-accent focus:ring-1 focus:ring-finance-accent transition-colors"
                                        placeholder="••••••••"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white focus:outline-none"
                                    >
                                        {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Confirm New Password</label>
                                <div className="relative">
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="w-full bg-finance-dark text-white border border-gray-800 rounded-lg px-4 py-3 focus:outline-none focus:border-finance-accent focus:ring-1 focus:ring-finance-accent transition-colors"
                                        placeholder="••••••••"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white focus:outline-none"
                                    >
                                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end pt-4 border-t border-gray-800">
                            <button
                                type="submit"
                                disabled={saving}
                                className="px-6 py-3 bg-finance-accent hover:bg-yellow-500 text-finance-dark font-bold rounded-xl transition-all shadow-lg shadow-finance-accent/20 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {saving ? (
                                    <div className="w-5 h-5 border-2 border-finance-dark border-r-transparent rounded-full animate-spin"></div>
                                ) : (
                                    <Save className="w-5 h-5" />
                                )}
                                Save Changes
                            </button>
                        </div>
                    </form>
                </div>

                {/* Right Column: Plan Information */}
                <div className="space-y-6">
                    <div className="bg-gradient-to-b from-[#0b0f19] to-finance-card border border-gray-800 rounded-2xl p-6 lg:p-8 hidden md:block relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-4 opacity-5">
                            <Shield className="w-32 h-32 text-finance-accent" />
                        </div>

                        <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2 relative z-10">
                            <CreditCard className="text-finance-accent w-5 h-5" />
                            Current Plan
                        </h2>

                        <div className="relative z-10">
                            <div className="flex items-end gap-2 mb-2">
                                <span className="text-4xl font-black text-finance-accent uppercase tracking-tight">{profile?.tier || 'Free'}</span>
                            </div>
                            <p className="text-sm text-gray-400 mb-6">
                                {profile?.role === 'admin' ? 'Administrative Access Granted' : 'Your current active subscription tier.'}
                            </p>

                            {profile?.tier !== 'Free' && profile?.tier !== 'admin' && (
                                <div className="space-y-4 border-t border-gray-800 pt-6">
                                    <div>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-gray-400">Monthly Analysis Usage</span>
                                            <span className="text-white font-mono">
                                                {profile?.monthly_checks_used ?? 0} / {
                                                    ['elite', 'Elite'].includes(profile?.subscription_id || profile?.tier) ? '250'
                                                        : ['pro', 'Pro', 'premium', 'Premium'].includes(profile?.subscription_id || profile?.tier) ? '70'
                                                            : '0'
                                                }
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-900 rounded-full h-2">
                                            <div
                                                className="bg-finance-accent h-2 rounded-full"
                                                style={{
                                                    width: `${Math.min(100, Math.max(0, ((profile?.monthly_checks_used || 0) / (
                                                        ['elite', 'Elite'].includes(profile?.subscription_id || profile?.tier) ? 250
                                                            : ['pro', 'Pro', 'premium', 'Premium'].includes(profile?.subscription_id || profile?.tier) ? 70
                                                                : 1
                                                    )) * 100))}%`
                                                }}
                                            ></div>
                                        </div>
                                    </div>

                                    <div className="pt-2">
                                        <div className="text-sm text-gray-400">Account Role</div>
                                        <div className="text-md text-white font-medium capitalize">{profile?.role}</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};
